/**
 * sucheRecents — der Speicher der ZULETZT GESUCHTEN BEGRIFFE der mobilen Suche
 * (Suche mobil v2, Stufe 3, Plan §6.2).
 *
 * @description
 *   Reine Lese- und Schreibhilfen um EINEN `localStorage`-Schluessel. Sie
 *   liegen bewusst ausserhalb der Kopfleiste: `SidebarAndHeaderBar.jsx` ist
 *   3789 Zeilen gross und ohne Kopfleiste nicht renderbar, diese Regeln sind
 *   aber ohne React pruefbar (Jest, `sucheRecents.test.js`).
 *
 *   🔴 **Gespeichert wird NUR der Begriff** — nie Trefferinhalte, Titel,
 *   E-Mails oder `href` (§6.3). Der Begriff kann eine Kunden-E-Mail oder ein
 *   Kundenname sein (W9), deshalb bleibt der Schluessel auf dem Geraet: kein
 *   HTTP-Aufruf, keine Uebertragung, keine Auswertung, kein Logeintrag. Er
 *   verschwindet mit `loescheRecents()`.
 *
 *   🔴 **Ein eigener Schluessel, nicht der Desktop-Schluessel.** Der traegt
 *   Treffer-OBJEKTE (`global_recent_searches`); ein String darin braeche die
 *   Desktop-Anzeige (§6.3, eigene Akte
 *   `docs/backlog-auto-repair/2026-10-31-kopfsuche-zuletzt-gesucht-speichert-trefferinhalte.md`).
 *
 *   🔴 **Beide Seiten in `try/catch`.** Safari im privaten Modus wirft bei
 *   `setItem` einen `QuotaExceededError`; ein gesperrter Speicher wirft schon
 *   beim Lesen. Beide Faelle ergeben eine LEERE Liste statt einer Ausnahme —
 *   die Suche bleibt benutzbar, sie merkt sich nur nichts.
 *
 * @useWhen
 *   - Zuletzt gesuchte Begriffe der mobilen Suche lesen, merken, loeschen
 *
 * @dontUseFor
 *   - Die Desktop-Suche — dort gilt `global_recent_searches` (Treffer-Objekte)
 *   - Trefferinhalte jeder Art: hier steht ausschliesslich der getippte Begriff
 *
 * @related
 *   - SidebarAndHeaderBar — einziger Aufrufer (mobiler Suchzweig)
 *
 * @reuseHints
 *   - Eine zweite „zuletzt benutzt"-Liste mit reinen Begriffen wuerde diese
 *     Datei TEILEN (Schluessel als Parameter), nicht kopieren
 */

/** Der Schluessel in `localStorage`. Nur Begriffe, nichts sonst. */
export const RECENT_QUERIES_KEY = 'pog.mobile.recentQueries'

/** Obergrenze der Anzeige (§6.2). */
export const RECENT_QUERIES_MAX = 8

/** Laengste gespeicherte Form eines Begriffs (§6.2). */
export const RECENT_BEGRIFF_MAX_LAENGE = 64

/** Kuerzer als das ist kein Begriff (§6.2: unter 2 Zeichen nichts). */
export const RECENT_BEGRIFF_MIN_LAENGE = 2

/**
 * Trimmt und kuerzt einen Begriff; zu kurze oder nicht-textuelle Eingaben
 * ergeben `''`.
 *
 * @param {unknown} roh der getippte Text
 * @returns {string} der speicherbare Begriff oder `''`
 */
export function normalisiereBegriff(roh) {
  if (typeof roh !== 'string') return ''
  const getrimmt = roh.trim()
  if (getrimmt.length < RECENT_BEGRIFF_MIN_LAENGE) return ''
  return getrimmt.slice(0, RECENT_BEGRIFF_MAX_LAENGE)
}

/**
 * Entdoppt (ohne Gross/Klein) und deckelt. Die REIHENFOLGE der Eingabe gilt:
 * der erste Treffer eines Begriffspaars gewinnt seinen Platz (§6.2, neueste
 * zuerst).
 *
 * @param {string[]} begriffe bereits normalisiert, neueste zuerst
 * @returns {string[]}
 */
function entdopple(begriffe) {
  const gesehen = new Set()
  const out = []
  for (const begriff of begriffe) {
    const schluessel = begriff.toLowerCase()
    if (gesehen.has(schluessel)) continue
    gesehen.add(schluessel)
    out.push(begriff)
    if (out.length >= RECENT_QUERIES_MAX) break
  }
  return out
}

/**
 * Liest die gespeicherten Begriffe. Wirft nie; ein unlesbarer oder gesperrter
 * Speicher ergibt `[]`.
 *
 * @returns {string[]} neueste zuerst, hoechstens `RECENT_QUERIES_MAX`
 */
export function ladeRecents() {
  try {
    const roh = localStorage.getItem(RECENT_QUERIES_KEY)
    if (!roh) return []
    const gelesen = JSON.parse(roh)
    if (!Array.isArray(gelesen)) return []
    // 🔴 Altbestand und fremde Schreiber: nur Strings zaehlen, und jeder wird
    // erneut normalisiert — ein zu langer oder zu kurzer Eintrag faellt hier.
    return entdopple(gelesen.map(normalisiereBegriff).filter(Boolean))
  } catch {
    return []
  }
}

/**
 * Merkt einen Begriff: er rueckt nach VORN, Duplikate (ohne Gross/Klein)
 * verschwinden, die Liste bleibt bei `RECENT_QUERIES_MAX`.
 *
 * @param {unknown} begriff der getippte Text
 * @returns {string[]} die neue Liste; `[]`, wenn der Speicher nicht schreibbar
 *   ist (dann ist auch nichts gemerkt)
 */
export function merkeRecent(begriff) {
  const neu = normalisiereBegriff(begriff)
  if (!neu) return ladeRecents()
  const liste = entdopple([neu, ...ladeRecents()])
  try {
    localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(liste))
    return liste
  } catch {
    // Voller oder gesperrter Speicher: nichts gemerkt, nichts geworfen.
    return []
  }
}

/**
 * Leert den Schluessel (§6.2 „Loeschen" — ohne Rueckfrage).
 *
 * @returns {string[]} immer `[]` (die neue Anzeige)
 */
export function loescheRecents() {
  try {
    localStorage.removeItem(RECENT_QUERIES_KEY)
  } catch {
    // Ignorieren: der Speicher ist nicht erreichbar, also ist auch nichts zu
    // loeschen — die Anzeige leert sich trotzdem.
  }
  return []
}
